/**
 * Punch Locations — plots every punch recorded on one day of a timesheet.
 *
 * Opened from the Map button on a Week Detail day-card header. Unlike the existing
 * per-punch GPS-exception modal (which this does not replace), it shows the whole day:
 * clean punches, flagged punches, each job's geofence, and the stops where an employee
 * punched out and back in without moving.
 *
 * Design: docs/featureBriefs/design_handoff_day_punch_map/README.md
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Circle, CircleMarker, MapContainer, Marker, Polyline, TileLayer, Tooltip, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import { AlertTriangle, Clock, LogIn, LogOut, MapPin, Navigation, X } from 'lucide-react';
import ProjectsAPI from '../../api/projects';
import { Day } from '../../interfaces/EmployeeInterface';
import type { NearestProject } from '../../interfaces/Project';
import {
  PunchEvent,
  PunchStop,
  buildPunchEvents,
  clusterStops,
  formatDistance,
  stopHeadline,
  stopLabel,
} from '../../util/dayPunchMap';
import { MAP_TILE_ATTRIBUTION, MAP_TILE_MAX_ZOOM, MAP_TILE_URL } from '../projectMapLeaflet';
import 'leaflet/dist/leaflet.css';
import './DayPunchMapModal.css';

const C_IN = '#1f9d6b';
const C_OUT = '#d8852f';
const C_FAR = '#e0533f';
const C_SITE = '#2f6fed';
/** Nearest active job — distinct from the punched job's blue. */
const C_NEAR = '#7c4dde';

/** Screen-space separation a pin needs before it stops being pushed outward. */
const MIN_PIN_GAP_PX = 38;
const MIN_PAIR_GAP_PX = 50;
const MAX_DECLUTTER_PASSES = 14;

function stopColor(stop: PunchStop): string {
  if (stop.outsideGeofence) return C_FAR;
  if (stop.events.length === 1) return stop.events[0].side === 'in' ? C_IN : C_OUT;
  return `linear-gradient(90deg, ${C_OUT} 50%, ${C_IN} 50%)`;
}

function pinIcon(stop: PunchStop, selected: boolean): L.DivIcon {
  const pair = stop.events.length > 1;
  const cls = `dm-pin${pair ? ' pair' : ''}${selected ? ' big' : ''}`;
  return L.divIcon({
    className: '',
    iconSize: pair ? [42, 26] : [26, 26],
    iconAnchor: pair ? [21, 13] : [13, 13],
    html: `<div class="${cls}" style="background:${stopColor(stop)}">${stopLabel(stop)}</div>`,
  });
}

const siteIcon = L.divIcon({
  className: '',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  html: '<div class="dm-sitepin"></div>',
});

const nearSiteIcon = L.divIcon({
  className: '',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  html: '<div class="dm-sitepin near"></div>',
});

/** Nearest active job to every stop — fetched once per open, in one request. */
type NearestState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'done'; nearest: Array<NearestProject | null>; activeWithoutCoordinates: number };

/** Vista job numbers arrive space-padded ("  0430-000"). */
function nearestJobLabel(p: NearestProject): string {
  return [p.altProjectID?.trim(), p.city?.trim()].filter(Boolean).join(' · ');
}

/** Same job the punch was recorded against — compared by location, which PunchSite carries. */
function isPunchedJob(stop: PunchStop, job: NearestProject): boolean {
  return !!stop.site
    && Math.abs(stop.site.lat - job.latitude) < 1e-6
    && Math.abs(stop.site.lng - job.longitude) < 1e-6;
}

interface NearestJobCardProps {
  stop: PunchStop;
  state: NearestState;
  job: NearestProject | null;
  shown: boolean;
  onToggle: () => void;
  onRetry: () => void;
}

/**
 * The nearest active job under each punch. Fixed height in every state so rows
 * don't move when results arrive.
 */
function NearestJobCard({ stop, state, job, shown, onToggle, onRetry }: NearestJobCardProps): React.ReactElement {
  let body: React.ReactNode;
  if (state.status === 'loading') {
    body = <span className="dm-nj-msg">Finding nearest active job…</span>;
  } else if (state.status === 'error') {
    body = (
      <span className="dm-nj-msg err">
        Couldn&rsquo;t load nearest job.{' '}
        <button type="button" className="dm-nj-link" onClick={(e) => { e.stopPropagation(); onRetry(); }}>Retry</button>
      </span>
    );
  } else if (!job) {
    body = <span className="dm-nj-msg">No active jobs have a location.</span>;
  } else {
    const fence = job.withinRadius
      ? 'In geofence'
      : job.radiusMiles == null ? 'No geofence set' : `Outside ${job.radiusMiles} mi radius`;
    body = (
      <>
        <span className="dm-nj-name">
          <b>{job.name}</b>
          {isPunchedJob(stop, job) && <span className="dm-nj-tag">Punched job</span>}
        </span>
        <span className="dm-nj-sub">{nearestJobLabel(job) || '\u00a0'}</span>
        <span className={`dm-nj-fence ${job.withinRadius ? 'in' : 'out'}`}>
          {job.withinRadius ? <MapPin size={12} /> : <AlertTriangle size={12} />}
          {formatDistance(job.distanceMiles)} away · {fence}
        </span>
      </>
    );
  }
  return (
    <span className={`dm-nj${shown ? ' on' : ''}`}>
      <span className="dm-nj-head">
        <span className="dm-nj-label">Nearest active job</span>
        {state.status === 'done' && job && (
          <button
            type="button"
            className={`dm-nj-btn${shown ? ' on' : ''}`}
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
          >
            <Navigation size={12} />{shown ? 'Hide on map' : 'See on map'}
          </button>
        )}
      </span>
      <span className="dm-nj-body">{body}</span>
    </span>
  );
}

/** "Rosegate – Ph. 2 · 14090 FINISH LABOR" — the cost code is de-emphasised. */
function jobLabel(e: PunchEvent): React.ReactNode {
  const name = e.isMeal ? 'Meal break' : e.jobName || '—';
  return (
    <>
      {name}
      {!e.isMeal && e.costCode && <span className="meal"> · {e.costCode}</span>}
    </>
  );
}

function DistanceLine({ stop }: { stop: PunchStop }): React.ReactElement | null {
  // Bare piece punches have no job, so there is nothing to measure against.
  if (!stop.site || stop.distanceMiles == null) return null;
  const d = formatDistance(stop.distanceMiles);
  const radius = stop.site.radiusMiles;
  const onSite = !stop.outsideGeofence && radius != null && stop.distanceMiles <= radius;
  return (
    <span className={`dm-dist${stop.outsideGeofence ? ' far' : ''}`}>
      {stop.outsideGeofence ? <AlertTriangle size={12} /> : <MapPin size={12} />}
      {d} from {stop.site.name}
      {stop.outsideGeofence && radius != null && ` · outside ${radius} mi radius`}
      {onSite && ' · on site'}
    </span>
  );
}

interface StopRowProps {
  stop: PunchStop;
  index: number;
  selected: boolean;
  onSelect: (index: number) => void;
  nearest: React.ReactNode;
}

/** A div, not a button: the nearest-job card inside it has its own button. */
const StopRow = React.forwardRef<HTMLDivElement, StopRowProps>(
  ({ stop, index, selected, onSelect, nearest }, ref) => {
    const first = stop.events[0];
    const last = stop.events[stop.events.length - 1];
    const isPair = stop.events.length > 1;
    const gap = last.minutes - first.minutes;
    const headline = stopHeadline(stop);

    return (
      <div
        role="button"
        tabIndex={0}
        ref={ref}
        className={`dm-row${selected ? ' on' : ''}${stop.outsideGeofence ? ' far' : ''}`}
        onClick={() => onSelect(index)}
        onKeyDown={(e) => {
          if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onSelect(index);
          }
        }}
      >
        <span className={`dm-num${isPair ? ' pair' : ''}`} style={{ background: stopColor(stop) }}>
          {stopLabel(stop)}
        </span>
        <span>
          <span className="dm-top">
            {isPair ? (
              <>
                <span className="dm-side-chip pair"><MapPin size={12} />Same spot</span>
                {/* Both punches on one clock minute read as one time, not an arrow to itself. */}
                <span className="dm-time">{gap > 0 ? `${first.time} → ${last.time}` : first.time}</span>
                {gap > 0 && <span className="dm-gap">{gap} min</span>}
              </>
            ) : (
              <>
                <span className={`dm-side-chip ${first.side}`}>
                  {first.side === 'in' ? <LogIn size={12} /> : <LogOut size={12} />}
                  {first.side === 'in' ? 'In' : 'Out'}
                </span>
                <span className="dm-time">{first.time}</span>
              </>
            )}
          </span>
          <span className="dm-job">
            {isPair ? (
              <>
                <b>{headline}</b>
                {stop.events.map((e) => (
                  <span className="dm-leg" key={e.key}>
                    <span className={`lb ${e.side}`}>{e.side === 'in' ? 'In' : 'Out'}</span>
                    <span className="lt">{e.time} · {jobLabel(e)}</span>
                  </span>
                ))}
              </>
            ) : (
              jobLabel(first)
            )}
          </span>
          <DistanceLine stop={stop} />
          {stop.message && (
            <span className="dm-dist far"><AlertTriangle size={12} />{stop.message}</span>
          )}
          {nearest}
        </span>
      </div>
    );
  }
);
StopRow.displayName = 'StopRow';

/** Punches the device never located — listed so the map and the punch table agree. */
function UnplottedRow({ event }: { event: PunchEvent }): React.ReactElement {
  return (
    <div className="dm-row nogps">
      <span className="dm-num none"><MapPin size={12} /></span>
      <span>
        <span className="dm-top">
          <span className={`dm-side-chip ${event.side}`}>
            {event.side === 'in' ? <LogIn size={12} /> : <LogOut size={12} />}
            {event.side === 'in' ? 'In' : 'Out'}
          </span>
          <span className="dm-time">{event.time}</span>
        </span>
        <span className="dm-job">{jobLabel(event)}</span>
        <span className="dm-dist">
          No location recorded{event.isManual ? ' · added manually' : ''}
        </span>
      </span>
    </div>
  );
}

interface DayPunchMapModalProps {
  day: Day;
  dow: string;
  dateLabel: string;
  onClose: () => void;
}

export default function DayPunchMapModal({
  day,
  dow,
  dateLabel,
  onClose,
}: DayPunchMapModalProps): React.ReactElement {
  const [map, setMap] = useState<L.Map | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  /**
   * Where each pin is DRAWN, which is not where it was recorded. The true-point dots
   * and geofence circles always render from the real coordinates; only these move.
   */
  const [pinPositions, setPinPositions] = useState<L.LatLngLiteral[]>([]);
  const listRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<Array<HTMLDivElement | null>>([]);
  /** The grid cell the map fills — observed for the first non-zero size. */
  const mapBoxRef = useRef<HTMLDivElement | null>(null);

  const [nearest, setNearest] = useState<NearestState>({ status: 'loading' });
  const [nearestTry, setNearestTry] = useState(0);
  /** The stop whose nearest job is drawn on the map, if any. */
  const [shownNearest, setShownNearest] = useState<number | null>(null);

  const events = useMemo(() => buildPunchEvents(day), [day]);
  const stops = useMemo(() => clusterStops(events), [events]);
  const unplotted = useMemo(() => events.filter((e) => e.lat == null), [events]);

  /** One circle + teardrop per job, however many punches landed on it. */
  const sites = useMemo(() => {
    const byKey = new Map<string, NonNullable<PunchStop['site']>>();
    for (const s of stops) {
      if (s.site) byKey.set(`${s.site.name}|${s.site.lat}|${s.site.lng}`, s.site);
    }
    return [...byKey.values()];
  }, [stops]);

  const flaggedCount = useMemo(() => stops.filter((s) => s.outsideGeofence).length, [stops]);
  const pairCount = useMemo(() => stops.filter((s) => s.events.length > 1).length, [stops]);
  const plottedCount = events.length - unplotted.length;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /**
   * At a zoom wide enough to show jobs miles apart, every stop inside one site collapses
   * into a few pixels. Separate them in SCREEN space — walk in order and push each pin
   * out along a golden-angle spiral until it clears the ones already placed — then draw
   * a leader back to the dot still sitting on the true coordinate. Recomputed on zoom,
   * so pins converge on their real positions as the user zooms in.
   */
  const declutter = useCallback(() => {
    if (!map || stops.length === 0) return;
    const placed: L.Point[] = [];
    const collides = (candidate: L.Point, minGap: number): boolean =>
      placed.some((o) => o.distanceTo(candidate) < minGap);

    const next: L.LatLngLiteral[] = stops.map((stop, i) => {
      const truePt = map.latLngToLayerPoint([stop.lat, stop.lng]);
      const minGap = stop.events.length > 1 ? MIN_PAIR_GAP_PX : MIN_PIN_GAP_PX;
      let pt = truePt;
      for (let pass = 1; pass <= MAX_DECLUTTER_PASSES && collides(pt, minGap); pass += 1) {
        const angle = ((i * 137.5 + pass * 47) * Math.PI) / 180;
        const radius = 26 + pass * 7;
        pt = L.point(truePt.x + radius * Math.cos(angle), truePt.y + radius * Math.sin(angle));
      }
      placed.push(pt);
      const ll = map.layerPointToLatLng(pt);
      return { lat: ll.lat, lng: ll.lng };
    });
    setPinPositions(next);
  }, [map, stops]);

  /**
   * Size, then fit — in that order, and not before the container actually has a size.
   *
   * The map lives in a grid row whose height is not settled during the commit that
   * hands us the map instance, so Leaflet initialises believing it is 0×0. In that
   * state it requests no tiles at all (the pane renders grey) while markers still
   * paint, because they are positioned by absolute transform and simply overflow —
   * which is why the failure looks like "pins on a blank background" rather than an
   * error. A requestAnimationFrame is not reliably late enough; observing the element
   * is, because the first non-zero box is exactly the moment fitting becomes valid.
   */
  useEffect(() => {
    if (!map || stops.length === 0) return;
    const el = mapBoxRef.current;
    if (!el) return;
    const points: L.LatLngExpression[] = [
      ...stops.map((s) => [s.lat, s.lng] as L.LatLngExpression),
      ...sites.map((s) => [s.lat, s.lng] as L.LatLngExpression),
    ];
    let fitted = false;
    const sizeAndFit = () => {
      if (!el.clientWidth || !el.clientHeight) return;
      map.invalidateSize();
      if (!fitted) {
        fitted = true;
        if (points.length > 1) map.fitBounds(L.latLngBounds(points), { padding: [66, 66], maxZoom: 15 });
        else map.setView(points[0], 15);
      }
      declutter();
    };
    const ro = new ResizeObserver(sizeAndFit);
    ro.observe(el);
    sizeAndFit(); // already sized (e.g. a hot reload into an open modal)
    return () => ro.disconnect();
  }, [map, stops, sites, declutter]);

  useEffect(() => {
    if (!map) return;
    map.on('zoomend', declutter);
    return () => { map.off('zoomend', declutter); };
  }, [map, declutter]);

  useEffect(() => {
    if (stops.length === 0) return;
    let cancelled = false;
    setNearest({ status: 'loading' });
    ProjectsAPI.getNearestProjects(stops.slice(0, 50).map((st) => ({ lat: st.lat, lng: st.lng })))
      .then((data) => {
        if (!cancelled) setNearest({ status: 'done', ...data });
      })
      .catch(() => {
        if (!cancelled) setNearest({ status: 'error' });
      });
    return () => { cancelled = true; };
  }, [stops, nearestTry]);

  const nearestFor = useCallback(
    (i: number): NearestProject | null => (nearest.status === 'done' ? nearest.nearest[i] ?? null : null),
    [nearest]
  );

  /** Frame the punch and the job's whole geofence, centred between them. */
  const toggleNearest = useCallback((i: number) => {
    if (shownNearest === i) {
      setShownNearest(null);
      return;
    }
    const stop = stops[i];
    const job = nearestFor(i);
    setShownNearest(i);
    setSelected(i);
    if (!map || !stop || !job) return;
    // toBounds takes the square's side length in metres, i.e. the diameter.
    const jobLL = L.latLng(job.latitude, job.longitude);
    const bounds = job.radiusMiles != null
      ? jobLL.toBounds(job.radiusMiles * 1609.34 * 2)
      : L.latLngBounds(jobLL, jobLL);
    bounds.extend([stop.lat, stop.lng]);
    map.stop(); // a second click mid-animation would fit from the in-between view
    map.fitBounds(bounds, { padding: [48, 48], maxZoom: 16 });
  }, [map, stops, nearestFor, shownNearest]);

  const shownStop = shownNearest != null ? stops[shownNearest] ?? null : null;
  const shownJob = shownNearest != null ? nearestFor(shownNearest) : null;
  const missingCount = nearest.status === 'done' ? nearest.activeWithoutCoordinates : 0;

  /** List → map. One setView: panning and then zooming restarts the zoom animation
   *  from the pre-pan centre and lands off-target. */
  const selectFromList = useCallback((i: number) => {
    setSelected(i);
    const stop = stops[i];
    if (!map || !stop) return;
    map.setView([stop.lat, stop.lng], Math.max(map.getZoom(), 14), { animate: true });
  }, [map, stops]);

  /** Map → list. scrollIntoView would scroll the modal itself, not just the list. */
  const selectFromMap = useCallback((i: number) => {
    setSelected(i);
    const row = rowRefs.current[i];
    if (row && listRef.current) listRef.current.scrollTop = row.offsetTop - 60;
  }, []);

  const hasMap = stops.length > 0;

  return (
    <div className="etrc-overlay dm-overlay" onClick={onClose}>
      <div className="dm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dm-head">
          <span className="mi"><MapPin size={21} /></span>
          <div className="dm-head-text">
            <h3>Punch locations — {dow}</h3>
            <div className="dm-sub">
              {dateLabel} · <b>{plottedCount}</b> punch{plottedCount === 1 ? '' : 'es'} at{' '}
              <b>{stops.length}</b> location{stops.length === 1 ? '' : 's'}
              {flaggedCount > 0 && (
                <span className="flag"> · {flaggedCount} outside geofence</span>
              )}
            </div>
          </div>
          <button type="button" className="dm-close" onClick={onClose} title="Close">
            <X size={18} />
          </button>
        </div>

        <div className="dm-body">
          <div className="dm-map" ref={mapBoxRef}>
            {hasMap ? (
              <MapContainer
                ref={setMap}
                center={[stops[0].lat, stops[0].lng]}
                zoom={13}
                zoomSnap={0.25}
                zoomControl={false}
                scrollWheelZoom
              >
                <ZoomControl position="topright" />
                <TileLayer url={MAP_TILE_URL} attribution={MAP_TILE_ATTRIBUTION} maxZoom={MAP_TILE_MAX_ZOOM} />

                {sites.map((site) => (
                  <React.Fragment key={`${site.name}|${site.lat}|${site.lng}`}>
                    {site.radiusMiles != null && (
                      <Circle
                        center={[site.lat, site.lng]}
                        radius={site.radiusMiles * 1609.34}
                        pathOptions={{
                          color: C_SITE,
                          weight: 2,
                          dashArray: '6 5',
                          fillColor: C_SITE,
                          fillOpacity: 0.07,
                        }}
                      />
                    )}
                    <Marker position={[site.lat, site.lng]} icon={siteIcon}>
                      <Tooltip direction="top" offset={[0, -12]}>
                        {site.name}{site.altID ? ` · ${site.altID}` : ''}
                      </Tooltip>
                    </Marker>
                  </React.Fragment>
                ))}

                {stops.map((stop, i) => {
                  const drawn = pinPositions[i] ?? { lat: stop.lat, lng: stop.lng };
                  const moved =
                    Math.abs(drawn.lat - stop.lat) > 1e-9 || Math.abs(drawn.lng - stop.lng) > 1e-9;
                  return (
                    <React.Fragment key={stop.events[0].key}>
                      {moved && (
                        <Polyline
                          positions={[[stop.lat, stop.lng], [drawn.lat, drawn.lng]]}
                          pathOptions={{
                            color: stop.outsideGeofence ? C_FAR : '#7b8493',
                            weight: 1.5,
                            opacity: 0.85,
                            dashArray: '3 3',
                          }}
                        />
                      )}
                      <CircleMarker
                        center={[stop.lat, stop.lng]}
                        radius={3}
                        pathOptions={{
                          weight: 2,
                          color: '#fff',
                          fillColor: stop.outsideGeofence ? C_FAR : '#3d4a5a',
                          fillOpacity: 1,
                        }}
                      />
                      <Marker
                        position={[drawn.lat, drawn.lng]}
                        icon={pinIcon(stop, selected === i)}
                        zIndexOffset={100 + i}
                        riseOnHover
                        eventHandlers={{ click: () => selectFromMap(i) }}
                      >
                        <Tooltip direction="top" offset={[0, -14]}>
                          {stop.events.map((e) => (
                            <div key={e.key}>
                              <b>{e.side === 'in' ? 'In' : 'Out'} {e.time}</b>
                              {(e.isMeal || e.jobName) && ` · ${e.isMeal ? 'Meal break' : e.jobName}`}
                            </div>
                          ))}
                          {stop.site && stop.distanceMiles != null && (
                            <div>
                              {formatDistance(stop.distanceMiles)} from site
                              {stop.events.length > 1 && ' · same recorded point'}
                            </div>
                          )}
                        </Tooltip>
                      </Marker>
                    </React.Fragment>
                  );
                })}

                {shownStop && shownJob && (
                  <>
                    {shownJob.radiusMiles != null && (
                      <Circle
                        center={[shownJob.latitude, shownJob.longitude]}
                        radius={shownJob.radiusMiles * 1609.34}
                        interactive={false}
                        pathOptions={{ color: C_NEAR, weight: 2, dashArray: '6 5', fillColor: C_NEAR, fillOpacity: 0.06 }}
                      />
                    )}
                    <Polyline
                      positions={[[shownStop.lat, shownStop.lng], [shownJob.latitude, shownJob.longitude]]}
                      interactive={false}
                      pathOptions={{ color: C_NEAR, weight: 2.5, dashArray: '7 6' }}
                    />
                    <Marker position={[shownJob.latitude, shownJob.longitude]} icon={nearSiteIcon}>
                      <Tooltip direction="top" offset={[0, -12]}>
                        Nearest active job: {shownJob.name}
                        {shownJob.altProjectID?.trim() ? ` · ${shownJob.altProjectID.trim()}` : ''}
                      </Tooltip>
                    </Marker>
                  </>
                )}
              </MapContainer>
            ) : (
              <div className="dm-empty">No GPS points were recorded for this day.</div>
            )}
          </div>

          <aside className="dm-side">
            <div className="dm-side-head">
              <Clock size={14} />
              <span>Punches</span>
              <span className="n">
                {stops.length} stop{stops.length === 1 ? '' : 's'}
                {pairCount > 0 && ` · ${pairCount} shared`}
              </span>
            </div>
            <div className="dm-list" ref={listRef}>
              {stops.map((stop, i) => (
                <StopRow
                  key={stop.events[0].key}
                  ref={(el) => { rowRefs.current[i] = el; }}
                  stop={stop}
                  index={i}
                  selected={selected === i}
                  onSelect={selectFromList}
                  nearest={
                    <NearestJobCard
                      stop={stop}
                      state={nearest}
                      job={nearestFor(i)}
                      shown={shownNearest === i}
                      onToggle={() => toggleNearest(i)}
                      onRetry={() => setNearestTry((n) => n + 1)}
                    />
                  }
                />
              ))}
              {unplotted.map((e) => <UnplottedRow key={e.key} event={e} />)}
              {missingCount > 0 && (
                <div className="dm-nj-missing">
                  <AlertTriangle size={12} />
                  {missingCount} active job{missingCount === 1 ? '' : 's'} with no location {missingCount === 1 ? 'was' : 'were'} not checked.
                </div>
              )}
              {stops.length === 0 && unplotted.length === 0 && (
                <div className="dm-empty">No punches were recorded for this day.</div>
              )}
            </div>
            <div className="dm-legend">
              <span><i style={{ background: C_IN }} />Clock in</span>
              <span><i style={{ background: C_OUT }} />Clock out</span>
              <span><i style={{ background: C_FAR }} />Outside geofence</span>
              <span>
                <i style={{ background: `linear-gradient(90deg, ${C_OUT} 50%, ${C_IN} 50%)`, borderRadius: 3, width: 16 }} />
                Out &amp; back in, same spot
              </span>
              <span><i style={{ background: C_SITE }} />Job site</span>
              <span><i style={{ background: C_NEAR }} />Nearest active job</span>
            </div>
          </aside>
        </div>

        <div className="dm-foot">
          <span className="dm-note">
            Recorded on the device at punch time. Dashed circles are each job&rsquo;s allowed radius.
            Each punch lists the nearest active job by straight-line distance.
          </span>
          <button type="button" className="etrc-btn primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
